import { Injectable } from "@angular/core";
import { WebSocketsService } from "./web-sockets.service";
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  tap,
} from "rxjs";

import Swal, { SweetAlertIcon } from "sweetalert2";

import { API_ENDPOINTS } from "./api/api-endpoints";
import { ApiService } from "./api/api.service";

import { ToastService } from "./toast.service";

export interface NotificationMessage {
  type: string;
  message?: any;
  timestamp?: string;
  [key: string]: any;
}

interface NotificationList {
  count: number;
  next: string | null;
  previous: string | null;
  results: NotificationMessage[];
}

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private CountSubject = new BehaviorSubject<number>(0);
  count$ = this.CountSubject.asObservable();

  private MessageSubject = new BehaviorSubject<NotificationMessage[]>([]);
  message$ = this.MessageSubject.asObservable();

constructor(
  private webSocketService: WebSocketsService,
  private apiService: ApiService,
  private toast: ToastService,
) {}

  // =========================================================
  // gloget UI NOTIFICATIONS
  // =========================================================

//   private showToast(
//     icon: SweetAlertIcon,
//     title: string,
//     message = "",
//   ): void {
//     Swal.fire({
//       toast: true,
//       position: "top-end",
//       icon,
//       title,
//       text: message,
//       showConfirmButton: false,
//       showCloseButton: true,
//       timer: 4000,
//       timerProgressBar: true,
//       customClass: {
//         popup: "gloget-toast",
//         title: "gloget-toast-title",
//         htmlContainer: "gloget-toast-message",
//         icon: "gloget-toast-icon",
//         timerProgressBar: "gloget-toast-progress",
//         closeButton: "gloget-toast-close",
//       },
//       didOpen: (toast) => {
//         toast.addEventListener("mouseenter", Swal.stopTimer);
//         toast.addEventListener("mouseleave", Swal.resumeTimer);
//       },
//     });
//   }

success(title: string, message = ""): void {
  this.toast.success(title, message);
}

error(title: string, message = ""): void {
  this.toast.error(title, message);
}

warning(title: string, message = ""): void {
  this.toast.warning(title, message);
}

info(title: string, message = ""): void {
  this.toast.info(title, message);
}

  // =========================================================
  // WEBSOCKET NOTIFICATIONS
  // =========================================================

  getNotifications(): Observable<any> {
    return this.webSocketService.message$.pipe(
      tap((data) => {
        if (data.type === "echo") {
          return;
        }

        if (
          data.type === "Notification" ||
          data.type === "alert" ||
          data.type === "reminder"
        ) {
          setTimeout(() => {
            this.CountSubject.next(this.CountSubject.value + 1);

            this.MessageSubject.next([
              ...this.MessageSubject.value,
              data,
            ]);
          }, 5000);
        }
      }),
    );
  }

  getNotificationByType(type: string): Observable<any> {
    return this.webSocketService.onMessageType(type).pipe(
      tap((data) => {
        this.CountSubject.next(this.CountSubject.value + 1);

        this.MessageSubject.next([
          ...this.MessageSubject.value,
          data,
        ]);
      }),
    );
  }

  // =========================================================
  // SERVER NOTIFICATIONS
  // =========================================================

  getAllNotificationsFromServer(): Observable<any> {
    return this.apiService
      .request<NotificationList>(
        API_ENDPOINTS.notification.list,
        "GET",
      )
      .pipe(
        tap((res) => {
          const unread = res.results.filter(
            (n: any) => !n.is_read,
          );

          this.CountSubject.next(unread.length);
          this.MessageSubject.next(res.results);
        }),

        catchError(
          this.handleError("getSavedNotifications", {
            results: [],
            count: 0,
          }),
        ),
      );
  }

  getUnreadCount(): Observable<number> {
    return this.apiService
      .request<any>(
        API_ENDPOINTS.notification.unreadCount,
        "GET",
      )
      .pipe(
        tap((res) => {
          this.CountSubject.next(res.unread_count);
        }),

        catchError(
          this.handleError("getUnreadCount", 0),
        ),
      );
  }

  markOneAsRead(id: string): Observable<any> {
    return this.apiService
      .request(
        API_ENDPOINTS.notification.markAsRead(id),
        "POST",
      )
      .pipe(
        tap(() => {
          const updated = this.MessageSubject.value.map(
            (n: any) =>
              n.id === id
                ? { ...n, is_read: true }
                : n,
          );

          this.MessageSubject.next(updated);

          const unread = updated.filter(
            (n: any) => !n.is_read,
          ).length;

          this.CountSubject.next(unread);
        }),

        catchError(
          this.handleError("markAsRead", null),
        ),
      );
  }

  markLoginAsRead(id: string): void {
    const updated = this.MessageSubject.value.map(
      (n: any) =>
        n.id === id
          ? { ...n, is_read: true }
          : n,
    );

    this.MessageSubject.next(updated);

    const unread = updated.filter(
      (n: any) => !n.is_read,
    ).length;

    this.CountSubject.next(unread);
  }

  markAllAsRead(): Observable<any> {
    const messages = this.MessageSubject.value;

    if (!messages || messages.length === 0) {
      return of({
        status: "no_messages",
      });
    }

    if (
      messages.length > 0 &&
      messages.every(
        (msg) =>
          msg?.["notification_type"] === "Login",
      )
    ) {
      const updated = messages.map((msg) => ({
        ...msg,
        is_read: true,
      }));

      this.MessageSubject.next(updated);
      this.CountSubject.next(0);

      return of({
        status: "single_notification_handled",
      });
    }

    return this.apiService
      .request(
        API_ENDPOINTS.notification.markAllAsRead,
        "POST",
      )
      .pipe(
        tap(() => {
          const updated = messages.map((n: any) => ({
            ...n,
            is_read: true,
          }));

          this.MessageSubject.next(updated);
          this.CountSubject.next(0);
        }),

        catchError(
          this.handleError("markAllAsRead", null),
        ),
      );
  }

  clearAll(): Observable<any> {
    return this.apiService
      .request(
        API_ENDPOINTS.notification.clearAll,
        "DELETE",
      )
      .pipe(
        tap(() => {
          this.MessageSubject.next([]);
          this.CountSubject.next(0);
        }),

        catchError(
          this.handleError("clearAll", null),
        ),
      );
  }

  deleteNotification(id: string): Observable<any> {
    return this.apiService
      .request(
        API_ENDPOINTS.notification.delete(id),
        "DELETE",
      )
      .pipe(
        tap(() => {
          const updated =
            this.MessageSubject.value.filter(
              (n: any) => n.id !== id,
            );

          this.MessageSubject.next(updated);

          const unread = updated.filter(
            (n: any) => !n.is_read,
          ).length;

          this.CountSubject.next(unread);
        }),

        catchError(
          this.handleError("deleteNotification", null),
        ),
      );
  }

  deleteLoginNotification(id: string): void {
    const updated =
      this.MessageSubject.value.filter(
        (n: any) => n.id !== id,
      );

    this.MessageSubject.next(updated);

    const unread = updated.filter(
      (n: any) => !n.is_read,
    ).length;

    this.CountSubject.next(unread);
  }

  updatePreferences(data: any): Observable<any> {
    return this.apiService
      .request(
        API_ENDPOINTS.notification.preferences,
        "PUT",
        data,
      )
      .pipe(
        catchError(
          this.handleError("updatePreferences", null),
        ),
      );
  }

  // =========================================================
  // ERROR HANDLING
  // =========================================================

  private handleError(
    method: string,
    fallbackValue: any,
  ) {
    return (error: any) => {
      console.error(
        `NotificationService.${method} failed`,
        error,
      );

      return of(fallbackValue);
    };
  }
}